/* eslint-disable no-console */
import { useEffect, useMemo, useState } from 'react';

import {
  Add,
  AutoCompleteOption,
  ChakraBox,
  ChakraGridItem,
  ChakraSimpleGrid,
  EquipUIFireflyV2Theme,
  Reset,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import { HEADER_HEIGHT, PAGE_LAYOUT, PAGE_TOP_PADDING } from '~/lib/constants';
import localStorageConstants from '~/lib/constants/localStorage.constants';
import {
  CREATE_TASK_SOURCES,
  OrganizeBy,
} from '~/lib/constants/task.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import useLocalStorage from '~/lib/hooks/useLocalStorage';
import { getValidOrganizeByValue } from '~/lib/hooks/useTasks';
import {
  getEmptyTaskContent,
  getTaskParamsFromFilters,
  organizeByCategory,
  organizeByDate,
  organizeByPatient,
  organizeByPriorityScore,
  parseTaskParamFilterValues,
} from '~/lib/util/task.util';
import Loading from '~/pages/loading';
import { Path } from '~/routes/constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { TaskEvents } from '~/analytics/taskEvents';

import EmptyTaskList from './EmptyTaskList';
import TaskFilterActionBar, {
  FILTER_ACTION_BAR_HEIGHT,
} from './TaskFilterActionBar';
import TaskList from './TaskList';
import TaskListItem from './TaskList/TaskListItem';
import TasksHeader, { BUTTON_HEIGHT } from './TasksHeader';

const organizeByOptions: SelectOption[] = [
  { label: 'Due date', value: OrganizeBy.DUE_DATE },
  { label: 'Created date', value: OrganizeBy.CREATED_DATE },
  { label: 'Patient', value: OrganizeBy.PATIENT },
  { label: 'Category', value: OrganizeBy.CATEGORY },
  { label: 'Priority score', value: OrganizeBy.PRIORITY_SCORE },
];

export const TASKS_LIST_PADDING_TOP = 16;

const { primary } = EquipUIFireflyV2Theme.colors;

const Tasks = () => {
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const {
    storedValue: organizeByLocalStorage,
    setValue: setOrganizeByLocalStorage,
  } = useLocalStorage<OrganizeBy>(
    localStorageConstants.taskOrganizeBy,
    OrganizeBy.DUE_DATE,
  );

  const [organizeBy, setOrganizeBy] = useState<OrganizeBy>(
    getValidOrganizeByValue(organizeByLocalStorage, OrganizeBy.DUE_DATE),
  );

  const [tasksPageNumber, setTasksPageNumber] = useState<number>(1);

  const { setComponentVisibility } = useComponentPortalContext();

  const {
    categories,
    createTaskData,
    filters,
    getTasks,
    isTasksDataLoading,
    resetFiltersToBlank,
    tasks,
    totalTasksCount,
  } = useTaskContext();

  const fetchNextBatch = () => {
    setTasksPageNumber((page) => page + 1);
  };

  useEffect(() => {
    if (tasksPageNumber > 1) {
      const activeFilters = parseTaskParamFilterValues(filters);

      getTasks(
        getTaskParamsFromFilters(activeFilters),
        tasksPageNumber,
        organizeBy,
      );
    }
  }, [tasksPageNumber]);

  const checkFilterActive = (
    filterOptions: Record<string, string | string[]>,
  ) => Object.values(filterOptions).some((value) => value.length);

  const [isFilterActive, setIsFilterActive] = useState<boolean>(
    checkFilterActive(parseTaskParamFilterValues(filters)),
  );

  useEffect(() => {
    setOrganizeByLocalStorage(organizeBy);
  }, [organizeBy]);

  useEffect(() => {
    if (createTaskData?.taskExternalId) {
      const activeFilters = parseTaskParamFilterValues(filters);
      getTasks(getTaskParamsFromFilters(activeFilters), 1, organizeBy);
      setTasksPageNumber(1);
    }
  }, [createTaskData?.taskExternalId]);

  const organizeByMapper: Record<
    OrganizeBy,
    (tasks: Task[]) => Record<string, Task[]>
  > = {
    [OrganizeBy.CREATED_DATE]: (tasks: Task[]) => organizeByDate(tasks, false),
    [OrganizeBy.DUE_DATE]: (tasks: Task[]) => organizeByDate(tasks, true),
    [OrganizeBy.PATIENT]: organizeByPatient,
    [OrganizeBy.CATEGORY]: organizeByCategory,
    [OrganizeBy.PRIORITY_SCORE]: organizeByPriorityScore,
  };

  const organizeData = (
    tasks: Task[],
    organizeBy: OrganizeBy,
  ): Record<string, Task[]> =>
    organizeByMapper[organizeBy]?.(tasks) ?? ({} as Record<string, Task[]>);

  const organizedTasks = useMemo<Record<string, Task[]>>(
    () => organizeData(tasks ?? [], organizeBy),
    [tasks, organizeBy],
  );

  const onTaskSelect = (value: string) => {
    history.push({
      pathname: `${Path.MAUD_TASKS}/${value}`,
      state: {
        fromTaskRoute: true,
      },
    });
  };

  const taskData = useMemo<TaskGroup[]>(
    () =>
      Object.keys(organizedTasks).map((key) => {
        const [_userId, firstname, chosenname, lastname] = key.split('::');
        return {
          count: organizedTasks[key]?.length ?? 0,
          key,
          title: key.match(/::/g)
            ? `${firstname} ${chosenname ? `"${chosenname}"` : ''} ${lastname}`
            : key,
          items: (organizedTasks[key] ?? []).map((t: Task) => (
            <ChakraGridItem key={t.taskExternalId}>
              <TaskListItem onTaskSelect={onTaskSelect} task={t} />
            </ChakraGridItem>
          )),
        };
      }),
    [organizedTasks],
  );

  const handleReset = (): void => {
    trackEvent(TaskEvents.resetFiltersButtonClickedFromTasksListPanel, {
      Source: AnalyticsSourceValues.TASKS_LIST_PANEL,
    });
    resetFiltersToBlank();
    setIsFilterActive(false);
    getTasks({}, 1, organizeBy);
    setTasksPageNumber(1);
  };

  const onApplyFilters = (params: Record<string, string | string[]>): void => {
    setIsFilterActive(checkFilterActive(params));
    getTasks(params, 1, organizeBy);
    setTasksPageNumber(1);
  };

  const handleRemoveTag = (
    key: keyof TTaskFilters,
    option: AutoCompleteOption,
  ): void => {
    const newValue = filters[key].value.filter(
      ({ value }) => value !== option.value,
    );
    const newFilters = {
      ...filters,
      [key]: {
        ...filters[key],
        value: newValue,
      },
    };
    filters[key].setter(newValue);
    const activeFilters = parseTaskParamFilterValues(newFilters);
    getTasks(getTaskParamsFromFilters(activeFilters), 1, organizeBy);
    setTasksPageNumber(1);
  };

  // ensure that header is sticky and only the actual tasks are allowed to scroll
  // return to default behavior for all other pages
  useEffect(() => {
    const prev = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = prev;
    };
  }, []);

  const handleOnOrganizeSelect = (value: OrganizeBy) => {
    const activeFilters = parseTaskParamFilterValues(filters);
    getTasks(getTaskParamsFromFilters(activeFilters), 1, value);
    setOrganizeBy(value);
    setTasksPageNumber(1);
  };

  const actionButtonsData: ActionButton[] = [
    {
      key: 'resetFilter',
      buttonText: 'Reset filters',
      rightIcon: <Reset fill={primary[100]} height="16px" width="16px" />,
      onClick: handleReset,
    },
    {
      key: 'addNewTask',
      buttonText: 'Add new task',
      rightIcon: <Add fill={primary[100]} height="16px" width="16px" />,
      onClick: () => {
        trackEvent(TaskEvents.addNewTaskButtonClickedFromTasksListPanel, {
          source: AnalyticsSourceValues.TASKS_LIST_PANEL,
          taskId: createTaskData?.taskExternalId,
        });
        setComponentVisibility({
          component: PortalComponent.ADD_TASK_ACCORDION,
          data: {
            triggerSource: CREATE_TASK_SOURCES.TASKS_LIST_PANEL,
          },
          isExpanded: true,
          isVisible: true,
          showLinkedPatient: false,
        });
      },
    },
  ];

  const hasFilters = useMemo<boolean>(
    () => Object.values(filters).some(({ value }) => value.length),
    [filters],
  );

  const gridSpacing = 8;

  // TODO try removing this hardcode
  const heightAboveList =
    Number(HEADER_HEIGHT.replace('px', '')) +
    TASKS_LIST_PADDING_TOP +
    BUTTON_HEIGHT +
    gridSpacing * (hasFilters ? 2 : 1) +
    (hasFilters ? FILTER_ACTION_BAR_HEIGHT : 0) +
    36; // some buffer

  const listHeight = `calc(100vh - ${heightAboveList}px)`;

  return (
    <ChakraSimpleGrid
      gap={`${gridSpacing}px`}
      height={isTasksDataLoading && '100%'}
      paddingRight={PAGE_LAYOUT.rightSpacing}
      paddingTop={PAGE_TOP_PADDING}
    >
      {isTasksDataLoading ? (
        <Loading />
      ) : (
        <>
          <TasksHeader
            categories={(categories ?? []).map(
              ({ categoryExternalId, categoryName }: Category) => ({
                primaryText: categoryName,
                value: categoryExternalId,
              }),
            )}
            filters={filters}
            getTasks={onApplyFilters}
            onOrganizeSelect={handleOnOrganizeSelect}
            organizeOptions={organizeByOptions}
            organizeValue={
              organizeByOptions.find(
                ({ value }: SelectOption) => value === organizeBy,
              ).label
            }
          />
          {hasFilters && (
            <TaskFilterActionBar
              filters={filters}
              handleRemove={handleRemoveTag}
              handleReset={handleReset}
            />
          )}
          {(tasks ?? []).length > 0 ? (
            <TaskList
              data={taskData}
              fetchNextBatch={fetchNextBatch}
              height={listHeight}
              hideSectionHeaders={organizeBy === OrganizeBy.PRIORITY_SCORE}
              totalTasksCount={totalTasksCount}
            />
          ) : (
            <ChakraBox height={listHeight} overflowY="scroll">
              <EmptyTaskList
                actionButtons={
                  isFilterActive ? actionButtonsData : [actionButtonsData[1]]
                }
                description={getEmptyTaskContent('description', isFilterActive)}
                primaryText={getEmptyTaskContent('primaryText', isFilterActive)}
                secondaryText={getEmptyTaskContent(
                  'secondaryText',
                  isFilterActive,
                )}
                showGraphic={!isFilterActive}
              />
            </ChakraBox>
          )}
        </>
      )}
    </ChakraSimpleGrid>
  );
};

export default Tasks;
